import { useState } from "react";

import { useActiveContext } from "shared/ContextSwitcher/store";
import { addToastNotification } from "shared/Toasts/toastNotificationStore";
import { useAnalytics } from "src/components/analytics/analytics";
import { Icon } from "src/components/icon/icon";
import { useSession } from "src/components/session/session";
import { Button } from "src/components/ui-components/Button/Button";
import AmplitudeService from "src/services/amplitude";
import { compareCode, handleDifference } from "src/utils/parameters/code";
import { resultStatusError } from "src/utils/results/status";
import { QueryResult } from "src/utils/results/types";

import {
  useEditorDispatch,
  useEditorParameters,
  useEditorQuery,
  useEditorQueryCode,
  useEditorQueryHasEditPermission,
  useEditorQueryId,
} from "../context";
import { useEditorQueryPerformanceState } from "../status/useEditorQueryPerformanceState";
import { useEditorExecuteQuery } from "../useEditorExecuteQuery";
import { useEditorSaveQuery } from "../useEditorSaveQuery";
import { debugWandQuery } from "./graphql";
import { Tooltip } from "src/components/ui-components/Tooltip/Tooltip";
import { Badge } from "src/components/ui-components/Badge/Badge";
import { IconCoins } from "src/components/ui-components/Icons/IconCoins";

export function FixQuery(props: { result: QueryResult | undefined }) {
  const [isFetchingFixQuery, setIsFetchingFixQuery] = useState(false);
  const session = useSession();
  const activeContext = useActiveContext();
  const dispatch = useEditorDispatch();
  const query = useEditorQuery();
  const id = useEditorQueryId();
  const code = useEditorQueryCode();
  const parameters = useEditorParameters();
  const canEdit = useEditorQueryHasEditPermission();
  const { performance } = useEditorQueryPerformanceState();
  const { captureEvent } = useAnalytics();

  const saveQuery = useEditorSaveQuery({
    onQueryHasChangedError: () => {
      addToastNotification({
        level: "error",
        title: "Query execution failed",
        description: "Query has changed",
      });
    },
  });

  const executeQuery = useEditorExecuteQuery({
    activeContext,
    performance,
  });

  if (
    session === undefined ||
    activeContext === undefined ||
    query === undefined ||
    id === undefined ||
    parameters === undefined ||
    parameters.merged.length > 0 ||
    !canEdit
  ) {
    return null;
  }

  const statusError = resultStatusError(props.result);
  const parsedError = statusError?.split("[Execution ID")?.[0];

  if (parsedError === undefined) {
    return null;
  }

  const parsedErrorHasLineNumber =
    parsedError.includes("Line") || parsedError.includes("line");

  if (!parsedErrorHasLineNumber) {
    return null;
  }

  const updateQueryCodeAndParameters = (nextCode: string) => {
    const { difference, nextKeys, prevKeys } = compareCode(code, nextCode);

    const { nextParameters, updatedCode } = handleDifference({
      difference,
      nextCode,
      nextKeys,
      parameters,
      prevKeys,
    });

    dispatch({
      type: "replaceQueryCodeAndParameters",
      parameters: nextParameters,
      code: updatedCode ?? nextCode,
    });
  };

  const handleButtonClick = async () => {
    captureEvent("Query editor: LLM Debug query");
    AmplitudeService.trackEvent("Wand Debug Query Clicked");
    setIsFetchingFixQuery(true);

    try {
      const result = await debugWandQuery(code, parsedError, session);

      if (result === undefined) {
        return;
      }

      AmplitudeService.trackEvent("Wand Debug Query", {
        code,
        parsedError,
        sql: result.query,
      });

      updateQueryCodeAndParameters(result.query);

      await saveQuery({
        ...query,
        query: result.query,
      });

      await executeQuery(id, {
        refresh: true,
        metadata: {
          query_tags: ["llm"],
        },
      });
    } catch (error) {
      captureEvent("Query editor: LLM Debug query failed");
      AmplitudeService.trackEvent("Wand Debug Query Failed");
    } finally {
      setIsFetchingFixQuery(false);
    }
  };

  return (
    <Tooltip
      label={
        <Badge size="M" color="neutral" variant="filled">
          10
          <IconCoins />
        </Badge>
      }
      position="above-center"
    >
      <Button
        theme="primary"
        size="M"
        loading={isFetchingFixQuery}
        onClick={handleButtonClick}
      >
        <Icon icon="wand" />
        Fix Query
      </Button>
    </Tooltip>
  );
}
